import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Home, MapPin, GitCompareArrows, Star, Bell } from "lucide-react";
import { Mascot } from "@/components/Mascot";
import { getCompareIds, getFavorites } from "@/lib/storage";

const NAV = [
  { to: "/", label: "Inicio", icon: Home, testId: "nav-home" },
  { to: "/map", label: "Mapa", icon: MapPin, testId: "nav-map" },
  { to: "/compare", label: "Comparador", icon: GitCompareArrows, testId: "nav-compare" },
  { to: "/favorites", label: "Favoritos", icon: Star, testId: "nav-favorites" },
];

export const Layout = ({ children, alertsCount = 0 }) => {
  const loc = useLocation();
  const [compareCount, setCompareCount] = useState(getCompareIds().length);
  const [favCount, setFavCount] = useState(getFavorites().length);

  useEffect(() => {
    const onC = (e) => setCompareCount((e.detail || []).length);
    const onF = (e) => setFavCount((e.detail || []).length);
    window.addEventListener("creep:compare", onC);
    window.addEventListener("creep:favorites", onF);
    return () => {
      window.removeEventListener("creep:compare", onC);
      window.removeEventListener("creep:favorites", onF);
    };
  }, []);

  return (
    <div className="relative min-h-screen grain">
      {/* Top nav */}
      <header
        className="sticky top-0 z-40 border-b border-white/5"
        style={{ backdropFilter: "blur(24px) saturate(140%)", background: "rgba(9,9,11,0.72)" }}
        data-testid="app-header"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group">
            <div className="relative w-9 h-9">
              <Mascot variant="hero" size={36} />
            </div>
            <div className="leading-none">
              <div className="text-lg font-extrabold tracking-tight">CREEP</div>
              <div className="text-[9px] uppercase tracking-[0.32em] text-violet-300/60 mt-0.5">
                Pirineos
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon, testId }) => {
              const active = loc.pathname === to;
              const badge =
                to === "/compare" ? compareCount : to === "/favorites" ? favCount : 0;
              return (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={`relative px-3 py-2 rounded-full flex items-center gap-2 text-sm transition-colors duration-200 ${
                    active
                      ? "bg-violet-600/20 text-white border border-violet-500/40"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                  {badge > 0 && (
                    <span
                      className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-violet-500 text-white creep-glow"
                      data-testid={`${testId}-badge`}
                    >
                      {badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            {alertsCount > 0 && (
              <div
                data-testid="header-alerts-count"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full bg-violet-500/15 text-violet-200 border border-violet-500/30"
              >
                <Bell size={14} />
                <span>{alertsCount} alerta{alertsCount > 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
        </div>

        {/* Mobile top-bar has no nav — use bottom tab bar instead */}
      </header>

      {/* Bottom tab bar (mobile) */}
      <nav
        data-testid="bottom-tabs"
        className="md:hidden fixed bottom-0 inset-x-0 z-40"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-3 mb-3 rounded-2xl border border-white/10 flex items-center justify-around px-1 py-1"
          style={{ background: "rgba(9,9,11,0.85)", backdropFilter: "blur(20px) saturate(140%)" }}
        >
          {NAV.map(({ to, label, icon: Icon, testId }) => {
            const active = loc.pathname === to;
            const badge = to === "/compare" ? compareCount : to === "/favorites" ? favCount : 0;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testId}-mobile`}
                className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl transition-colors ${
                  active ? "text-white bg-violet-500/18" : "text-white/60"
                }`}
              >
                <Icon size={17} />
                <span className="text-[10px] font-medium">{label}</span>
                {badge > 0 && (
                  <span
                    data-testid={`${testId}-mobile-badge`}
                    className="absolute top-1 right-3 min-w-[16px] h-[16px] px-1 rounded-full bg-violet-500 text-[9px] font-bold text-white flex items-center justify-center creep-glow"
                  >
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      <main className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-5 pb-24 md:pb-10">{children}</main>

      <footer className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24 md:pb-8 text-center text-[10px] text-white/30 tracking-[0.28em] uppercase">
        Creep · Nieve de Cataluña y Pirineos
      </footer>
    </div>
  );
};

export default Layout;
